<template>
  <v-row no-gutters class="fill-height flex-column pr-5 pb-5">
    <card-title-row class="pl-0 pt-5" translation-path="sidebar.waterBalanceIwa.title" :has-buttons="false" />
    <water-balance-wizard
      ref="iwa-wizard"
      with-profiles
      use-analysis-date
      tool-name="iwa"
      :inputs="stepsInputs"
      :steps="steps"
      :analysis-date-type="params['iwa_type'] === 'monthly' ? 'month' : 'year'"
      :params-proxy.sync="params"
      :profiles-data="profilesData"
      @changeCurrentProfile="changeCurrentProfile"
    >
      <template #lastStep-prepend>
        <v-radio-group v-model="params['iwa_type']" class="mt-0" row>
          <v-radio :label="$i18n.t('sidebar.waterBalanceIwa.month')" value="monthly"></v-radio>
          <v-radio :label="$i18n.t('sidebar.waterBalanceIwa.year')" value="annual"></v-radio>
        </v-radio-group>
      </template>
    </water-balance-wizard>
  </v-row>
</template>

<script>
import WaterBalanceWizard from '@/components/WaterBalanceWizard';
import CardTitleRow from '@/components/CardTitleRow';
import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';
import { get, call } from 'vuex-pathify';

export default {
  name: 'WaterBalanceIwa',
  mixins: [prepareAttributesFeatureCardMixin],
  components: {
    WaterBalanceWizard,
    CardTitleRow,
  },
  data: () => ({
    params: {
      iwa_type: 'monthly',
      dma_estimate_value: false,
      dma_generate_save: false,
      dma_generate: false,
    },
  }),
  watch: {
    'params.iwa_type': function(nV) {
      if (nV === 'annual' && this.params['analysis_date'] !== undefined) {
        const year = this.params['analysis_date'] ? this.params['analysis_date'].split('-')[0] : this.$dayjs().year();
        this.params['analysis_date'] = `${year}-01-01`;
      }
    },
    'params.dma_generate': function(nV) {
      if (nV) {
        this.params['dma_estimate_value'] = true;
        if (!this.params['dma_population_value']) this.params['dma_population_value'] = 0;
      }
    },
  },
  methods: {
    getIwaProfiles: call(`waterBalance/getIwaProfiles`),
    saveIwaProfile: call(`waterBalance/saveIwaProfile`),
    editIwaProfile: call(`waterBalance/editIwaProfile`),
    deleteIwaProfile: call(`waterBalance/deleteIwaProfile`),
    changeCurrentProfile(nV) {
      if (nV) {
        this.params['dma_estimate_value'] =
          nV['dma_population_value'] || nV['dma_population_value'] === 0 ? true : false;
      }
    },
  },
  computed: {
    projectElements: get('layers/project@layers'),
    iwaProfiles: get('waterBalance/iwaProfiles'),
    iwaWaterUsageDemandSetting: get('admin/settingsValues@water_balance_iwa_demand_from_water_usage'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
    dmaLayers() {
      return this.projectLayers.filter(layer => ['linestring', 'multilinestring'].includes(layer.geometry_type));
    },
    pipesLayers() {
      return this.projectLayers.filter(layer => layer.data_source_name === 'water_pipes');
    },
    terminalsLayers() {
      return this.projectLayers.filter(layer => layer.data_source_name === 'water_terminals');
    },
    watermetersLayers() {
      return this.projectLayers.filter(layer => layer.data_source_name === 'water_watermeters');
    },
    waterPartitionsLayers() {
      return this.projectLayers.filter(layer => layer.data_source_name === 'water_partitions');
    },
    waterPointLayers() {
      return this.projectLayers.filter(
        layer => layer.layer_scope === 'water' && ['point', 'multipoint'].includes(layer.geometry_type)
      );
    },
    defaultInputProps() {
      return {
        dataType: { name: 'select' },
        rules: [v => !!v || [false, 0].includes(v) || this.$i18n.t('rules.required')],
        cols: '7',
        nameCols: '5',
      };
    },
    profilesData() {
      return {
        translationPaths: {
          addProfileDialogTitle: 'dialog.title.profiles.waterBalanceIwaAdd',
          editProfileDialogTitle: 'dialog.title.profiles.waterBalanceIwaEdit',
          deleteProfileDialogTitle: 'dialog.title.profiles.waterBalanceIwaDelete',
        },
        profiles: this.iwaProfiles,
        getProfiles: this.getIwaProfiles,
        saveProfile: this.saveIwaProfile,
        editProfile: this.editIwaProfile,
        deleteProfile: this.deleteIwaProfile,
      };
    },
    steps() {
      return [
        {
          step: 1,
          translationPath: 'sidebar.waterBalanceIwa.flowmeters',
        },
        {
          step: 2,
          translationPath: 'sidebar.waterBalanceIwa.dma',
        },
        {
          step: 3,
          translationPath: 'sidebar.waterBalanceIwa.pipes',
        },
        {
          step: 4,
          translationPath: 'sidebar.waterBalanceIwa.terminals',
        },
        {
          step: 5,
          translationPath: 'sidebar.waterBalanceIwa.waterPartitionPoints',
        },
        {
          step: 6,
          translationPath: 'sidebar.waterBalanceIwa.watermeters',
        },
        {
          step: 7,
          translationPath: 'sidebar.waterBalanceIwa.failures',
          subtitlePath: 'default.optional',
        },
        {
          step: 8,
          translationPath: 'sidebar.waterBalanceIwa.analysis',
        },
      ];
    },
    stepsInputs() {
      return [
        {
          name: 'flow_meter_layer',
          isLayer: true,
          step: 1,
          translationPath: 'sidebar.waterBalanceIwa.flowmetersLayer',
          items: this.waterPointLayers,
          itemText: 'name',
          itemValue: 'layer_id',
          disabled: this.waterPointLayers?.length === 0,
          tooltip:
            this.waterPointLayers?.length === 0 ? this.$i18n.t('sidebar.waterBalanceIwa.addFlowmetersToRun') : null,
          ...this.defaultInputProps,
        },
        {
          name: 'flow_meter_name',
          step: 1,
          connectedLayer: 'flow_meter_layer',
          translationPath: 'sidebar.waterBalanceIwa.flowmetersNameAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          disabled: !this.params['flow_meter_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'flow_meter_scada_flow',
          step: 1,
          connectedLayer: 'flow_meter_layer',
          translationPath: 'sidebar.waterBalanceIwa.scadaFlowAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          disabled: !this.params['flow_meter_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'flow_meter_scada_pressure',
          step: 1,
          connectedLayer: 'flow_meter_layer',
          translationPath: 'sidebar.waterBalanceIwa.scadaPressureAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          disabled: !this.params['flow_meter_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'dma_generate',
          step: 2,
          isNotAttribute: true,
          skipFormReset: true,
          label: this.$i18n.t('sidebar.waterBalanceIwa.autoDma'),
          dataType: { name: 'checkbox' },
          hasNameColumn: false,
          cols: '12',
        },
        ...(!this.params['dma_generate']
          ? [
              {
                name: 'dma_layer',
                isLayer: true,
                step: 2,
                translationPath: 'sidebar.waterBalanceIwa.dmaLayer',
                items: this.dmaLayers,
                itemText: 'name',
                itemValue: 'layer_id',
                disabled: this.dmaLayers?.length === 0,
                tooltip: this.dmaLayers?.length === 0 ? this.$i18n.t('sidebar.waterBalanceIwa.addDmaToRun') : null,
                ...this.defaultInputProps,
              },
              {
                name: 'dma_name',
                step: 2,
                connectedLayer: 'dma_layer',
                translationPath: 'sidebar.waterBalanceIwa.dmaNameAttribute',
                itemText: 'label',
                itemValue: 'attribute',
                disabled: !this.params['dma_layer'],
                ...this.defaultInputProps,
              },
              {
                name: 'dma_pipes',
                step: 2,
                connectedLayer: 'dma_layer',
                translationPath: 'sidebar.waterBalanceIwa.assignPipesIdAttribute',
                itemText: 'label',
                itemValue: 'attribute',
                disabled: !this.params['dma_layer'],
                ...this.defaultInputProps,
              },
              {
                name: 'dma_inflow',
                step: 2,
                connectedLayer: 'dma_layer',
                translationPath: 'sidebar.waterBalanceIwa.dmaPositiveFlowAttribute',
                itemText: 'label',
                itemValue: 'attribute',
                subtitlePath: 'sidebar.waterBalanceIwa.dmaPositiveFlowSubtitle',
                hasSubtitle: true,
                hasSmallSubtitle: true,
                disabled: !this.params['dma_layer'],
                ...this.defaultInputProps,
              },
              {
                name: 'dma_outflow',
                step: 2,
                connectedLayer: 'dma_layer',
                translationPath: 'sidebar.waterBalanceIwa.dmaNegativeFlowAttribute',
                itemText: 'label',
                itemValue: 'attribute',
                subtitlePath: 'sidebar.waterBalanceIwa.dmaNegativeFlowSubtitle',
                hasSubtitle: true,
                hasSmallSubtitle: true,
                disabled: !this.params['dma_layer'],
                ...this.defaultInputProps,
              },
              {
                name: 'dma_population',
                step: 2,
                connectedLayer: 'dma_layer',
                translationPath: 'sidebar.waterBalanceIwa.dmaPopulationAttribute',
                itemText: 'label',
                itemValue: 'attribute',
                disabled: !this.params['dma_layer'] || this.params['dma_estimate_value'],
                ...this.defaultInputProps,
                rules: !this.params['dma_estimate_value']
                  ? [v => !!v || [false, 0].includes(v) || this.$i18n.t('rules.required')]
                  : [],
              },
            ]
          : [
              {
                name: 'dma_generate_save',
                step: 2,
                isNotAttribute: true,
                skipFormReset: true,
                label: this.$i18n.t('sidebar.waterBalanceIwa.saveDmaToLayer'),
                dataType: { name: 'checkbox' },
                hasNameColumn: false,
                cols: '12',
              },
            ]),
        {
          name: 'dma_estimate_value',
          step: 2,
          connectedValue: ['dma_population', 'dma_population_value'],
          isNotAttribute: true,
          skip: true,
          label: this.$i18n.t('sidebar.waterBalanceIwa.dmaEstimatePopulation'),
          dataType: { name: 'checkbox' },
          disabled: !this.params['dma_layer'] || this.params['dma_generate'],
          hasNameColumn: false,
          cols: '12',
        },
        {
          name: 'dma_population_value',
          step: 2,
          translationPath: 'sidebar.waterBalanceIwa.population',
          hidden: !this.params['dma_estimate_value'],
          disabled: !this.params['dma_generate'] ? !this.params['dma_layer'] : false,
          dataType: { name: 'integer' },
          autocomplete: 'off',
          rules: [v => !!v || [false, 0].includes(v) || this.$i18n.t('rules.required')],
          cols: '7',
          nameCols: '5',
        },
        {
          name: 'pipes_layer',
          isLayer: true,
          step: 3,
          translationPath: 'sidebar.waterBalanceIwa.pipesLayer',
          items: this.pipesLayers,
          itemText: 'name',
          itemValue: 'layer_id',
          disabled: this.pipesLayers?.length === 0,
          tooltip: this.pipesLayers?.length === 0 ? this.$i18n.t('sidebar.waterBalanceIwa.addPipesToRun') : null,
          ...this.defaultInputProps,
        },
        {
          name: 'pipes_analyze',
          step: 3,
          connectedValue: ['pipes_analyze_values'],
          connectedLayer: 'pipes_layer',
          translationPath: 'sidebar.waterBalanceIwa.analysisAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          disabled: !this.params['pipes_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'pipes_analyze_values',
          step: 3,
          connectedAttribute: 'pipes_analyze',
          translationPath: 'sidebar.waterBalanceIwa.analysisValues',
          multiple: true,
          chips: true,
          autoConvertSelect: false,
          disabled: !this.params['pipes_layer'] || !this.params['pipes_analyze'],
          ...this.defaultInputProps,
        },
        {
          name: 'terminals_layer',
          isLayer: true,
          step: 4,
          translationPath: 'sidebar.waterBalanceIwa.terminalsLayer',
          items: this.terminalsLayers,
          itemText: 'name',
          itemValue: 'layer_id',
          disabled: this.terminalsLayers?.length === 0,
          tooltip:
            this.terminalsLayers?.length === 0 ? this.$i18n.t('sidebar.waterBalanceIwa.addTerminalsToRun') : null,
          ...this.defaultInputProps,
        },
        {
          name: 'terminals_parent_pipe',
          step: 4,
          connectedLayer: 'terminals_layer',
          translationPath: 'sidebar.waterBalanceIwa.pipeIdAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          disabled: !this.params['terminals_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'terminals_analyze',
          step: 4,
          connectedLayer: 'pipes_layer',
          connectedValue: ['terminals_analyze_values'],
          translationPath: 'sidebar.waterBalanceIwa.analysisAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          disabled: !this.params['terminals_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'terminals_analyze_values',
          step: 4,
          connectedAttribute: 'terminals_analyze',
          checked: true,
          translationPath: 'sidebar.waterBalanceIwa.analysisValues',
          multiple: true,
          chips: true,
          autoConvertSelect: false,
          disabled: !this.params['terminals_layer'] || !this.params['terminals_analyze'],
          ...this.defaultInputProps,
        },
        {
          name: 'partitions_layer',
          isLayer: true,
          step: 5,
          translationPath: 'sidebar.waterBalanceIwa.waterPartitionLayer',
          items: this.waterPartitionsLayers,
          itemText: 'name',
          itemValue: 'layer_id',
          disabled: this.waterPartitionsLayers?.length === 0,
          tooltip:
            this.waterPartitionsLayers?.length === 0
              ? this.$i18n.t('sidebar.waterBalanceIwa.addPartitionsToRun')
              : null,
          ...this.defaultInputProps,
        },
        {
          name: 'partitions_parent_pipe_layer',
          step: 5,
          connectedLayer: 'partitions_layer',
          translationPath: 'sidebar.waterBalanceIwa.waterPartitionAssignLayer',
          itemText: 'label',
          itemValue: 'attribute',
          disabled: !this.params['partitions_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'partitions_parent_pipe',
          step: 5,
          connectedLayer: 'partitions_layer',
          translationPath: 'sidebar.waterBalanceIwa.assignIdAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          disabled: !this.params['partitions_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'watermeters_layer',
          isLayer: true,
          step: 6,
          translationPath: 'sidebar.waterBalanceIwa.watermetersLayer',
          items: this.watermetersLayers,
          itemText: 'name',
          itemValue: 'layer_id',
          disabled: this.watermetersLayers?.length === 0,
          tooltip:
            this.watermetersLayers?.length === 0 ? this.$i18n.t('sidebar.waterBalanceIwa.addWatermetersToRun') : null,
          ...this.defaultInputProps,
        },
        ...(this.iwaWaterUsageDemandSetting
          ? [
              {
                name: 'watermeters_billing_id',
                step: 6,
                connectedLayer: 'watermeters_layer',
                translationPath: 'sidebar.waterBalanceIwa.watermeterIdBilling',
                itemText: 'label',
                itemValue: 'attribute',
                disabled: !this.params['watermeters_layer'],
                ...this.defaultInputProps,
              },
            ]
          : [
              {
                name: 'watermeters_usage',
                step: 6,
                connectedLayer: 'watermeters_layer',
                translationPath: 'sidebar.waterBalanceIwa.averageConsumptionAttribute',
                itemText: 'label',
                itemValue: 'attribute',
                disabled: !this.params['watermeters_layer'],
                ...this.defaultInputProps,
              },
            ]),
        {
          name: 'watermeters_parent_partition',
          step: 6,
          connectedLayer: 'watermeters_layer',
          translationPath: 'sidebar.waterBalanceIwa.watermetersParentPartition',
          itemText: 'label',
          itemValue: 'attribute',
          disabled: !this.params['watermeters_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'watermeters_main_watermeter',
          step: 6,
          connectedLayer: 'watermeters_layer',
          connectedValue: ['watermeters_main_watermeter_value'],
          translationPath: 'sidebar.waterBalanceIwa.mainWatermetersAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          disabled: !this.params['watermeters_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'watermeters_main_watermeter_value',
          step: 6,
          connectedAttribute: 'watermeters_main_watermeter',
          translationPath: 'sidebar.waterBalanceIwa.mainAndCommonWatermetersValue',
          disabled: !this.params['watermeters_layer'] || !this.params['watermeters_main_watermeter'],
          ...this.defaultInputProps,
        },
        {
          name: 'watermeters_type',
          step: 6,
          connectedLayer: 'watermeters_layer',
          connectedValue: ['watermeters_sold_values', 'watermeters_domestic_values', 'watermeters_internal_values'],
          translationPath: 'sidebar.waterBalanceIwa.typeAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          disabled: !this.params['watermeters_layer'],
          ...this.defaultInputProps,
        },
        {
          name: 'watermeters_sold_values',
          step: 6,
          connectedAttribute: 'watermeters_type',
          translationPath: 'sidebar.waterBalanceIwa.soldWaterValue',
          multiple: true,
          chips: true,
          autoConvertSelect: false,
          disabled: !this.params['watermeters_layer'] || !this.params['watermeters_type'],
          ...this.defaultInputProps,
        },
        {
          name: 'watermeters_domestic_values',
          step: 6,
          connectedAttribute: 'watermeters_type',
          translationPath: 'sidebar.waterBalanceIwa.houseTypeValue',
          multiple: true,
          chips: true,
          autoConvertSelect: false,
          disabled: !this.params['watermeters_layer'] || !this.params['watermeters_type'],
          ...this.defaultInputProps,
        },
        {
          name: 'watermeters_internal_values',
          step: 6,
          connectedAttribute: 'watermeters_type',
          translationPath: 'sidebar.waterBalanceIwa.ownPurposeValue',
          multiple: true,
          chips: true,
          autoConvertSelect: false,
          disabled: !this.params['watermeters_layer'] || !this.params['watermeters_type'],
          ...this.defaultInputProps,
        },
        {
          name: 'failures_layer',
          isLayer: true,
          step: 7,
          translationPath: 'sidebar.waterBalanceIwa.failuresLayer',
          items: this.pipesLayers,
          itemText: 'name',
          itemValue: 'layer_id',
          disabled: this.pipesLayers?.length === 0,
          tooltip: this.pipesLayers?.length === 0 ? this.$i18n.t('sidebar.waterBalanceIwa.addPipesToRun') : null,
          ...this.defaultInputProps,
          clearable: true,
          rules: [],
        },
        {
          name: 'failures_analyze',
          step: 7,
          connectedValue: ['failures_analyze_value'],
          connectedLayer: 'failures_layer',
          translationPath: 'sidebar.waterBalanceIwa.analysisAttribute',
          itemText: 'label',
          itemValue: 'attribute',
          disabled: !this.params['failures_layer'],
          ...this.defaultInputProps,
          rules: this.params['failures_layer']
            ? [v => !!v || [false, 0].includes(v) || this.$i18n.t('rules.required')]
            : [],
        },
        {
          name: 'failures_analyze_value',
          step: 7,
          connectedAttribute: 'failures_analyze',
          translationPath: 'sidebar.waterBalanceIwa.analysisValue',
          disabled: !this.params['failures_layer'] || !this.params['failures_analyze'],
          ...this.defaultInputProps,
          rules: this.params['failures_layer']
            ? [v => !!v || [false, 0].includes(v) || this.$i18n.t('rules.required')]
            : [],
        },
        {
          name: 'failures_date',
          step: 7,
          connectedLayer: 'failures_layer',
          translationPath: 'sidebar.waterBalanceIwa.failuresDate',
          itemText: 'label',
          itemValue: 'attribute',
          disabled: !this.params['failures_layer'],
          ...this.defaultInputProps,
          rules: this.params['failures_layer']
            ? [v => !!v || [false, 0].includes(v) || this.$i18n.t('rules.required')]
            : [],
        },
      ];
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .v-stepper--alt-labels .v-stepper__header .v-stepper__step[data-v-1a33ef5e] {
    min-width: 90px !important;
  }
}
</style>
